import {Component, EffectRef, EventEmitter, Input, OnDestroy, OnInit, Output, WritableSignal, effect, inject, signal, untracked} from '@angular/core';
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {FormCheckboxComponent} from "../../../shared/elements/form-checkbox/form-checkbox.component";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {FormTextareaComponent} from "../../../shared/elements/form-textarea/form-textarea.component";
import {FormUserGroupMailComponent} from "../../../shared/elements/form-user-group-mail/form-user-group-mail.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {NgOptimizedImage} from "@angular/common";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {TYPE} from "../../../shared/types/types";
import {DialogService} from "../../../shared/services/dialog.service";
import {StepHeaderComponent} from "../../../shared/elements/step-header/step-header.component";
import {LanguageService} from "../../../shared/services/language.service";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {StepContentComponent} from "../../../shared/elements/step-content/step-content.component";
import {catchError, concatMap, from, map, of, Subscription, take, toArray} from "rxjs";
import {ApiService} from "../../../shared/services/api.service";
import {ErrorService} from "../../../shared/services/error.service";
import {LayoutService} from '../../../shared/services/layout.service';
import {Router} from '@angular/router';
import {DocumentSelectorComponent} from "../../documents/document-selector/document-selector.component";
import {FormDateTimeComponent} from "../../../shared/elements/form-date-time/form-date-time.component";
import {FormRadioComponent} from "../../../shared/elements/form-radio/form-radio.component";
import {KnowledgebaseContentResource} from '../../../shared/types/knowledgebase-types/knowledgebaseContentResource';
import {NewsStore} from "../../news/store/news-store";
import {ChatStore} from "../../chat/chat-store/chat-store";
import {NewsResource} from "../../../shared/types/news-types/newsResource";
import {NewsCreateRequest} from "../../../shared/types/news-types/newsCreateRequest";
import {ChatListResource} from "../../../shared/types/chat-types/chatListResource";
import {ToasterService} from "../../../shared/services/toaster.service";
import {ProfileService} from "../../../shared/services/profile.service";
import {CapabilitiesService} from "../../../shared/services/capabilities.service";
import {FileStorageResource} from '../../../shared/types/settings-types/fileStorageResource';

type ShareStepKey = 'news' | 'chat' | 'mail' | 'link' | 'tv';

@Component({
    selector: 'app-dialog-share',
    imports: [
        DialogModule,
        FormButtonComponent,
        FormCheckboxComponent,
        FormInputComponent,
        FormLabelComponent,
        FormTextareaComponent,
        FormUserGroupMailComponent,
        HeadlineComponent,
        IconBadgeComponent,
        LinkComponent,
        TooltipComponent,
        TooltipModule,
        TranslatePipe,
        StepHeaderComponent,
        StepContentComponent,
        NgOptimizedImage,
        DocumentSelectorComponent,
        FormDateTimeComponent,
        FormRadioComponent
    ],
    templateUrl: './dialog-share.component.html',
    styleUrl: './dialog-share.component.scss'
})
export class DialogShareComponent implements OnInit, OnDestroy {
  private static readonly HELP_STEP_INDEX_BY_KEY: Record<ShareStepKey, number> = {
    news: 0,
    chat: 1,
    mail: 2,
    link: 3,
    tv: 4
  };
  //#region injections
  public dialogService: DialogService = inject(DialogService);
  public layoutService: LayoutService = inject(LayoutService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private languageService: LanguageService = inject(LanguageService);
  private apiService: ApiService = inject(ApiService);
  private errorService: ErrorService = inject(ErrorService);
  private toasterService: ToasterService = inject(ToasterService);
  private profileService: ProfileService = inject(ProfileService);
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);
  private router: Router = inject(Router);
  private newsStore: InstanceType<typeof NewsStore> = inject(NewsStore);
  private chatStore: InstanceType<typeof ChatStore> = inject(ChatStore);
  //#endregion injections

  //#region IO
  @Input() public data: (NewsResource | KnowledgebaseContentResource | TYPE.CommunityDetailEntry) | null = null;
  @Input() public communityDetailEntry: TYPE.CommunityDetailEntry | null = null;
  @Input() public visible: boolean = false;
  @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  //#endregion IO

  //#region dialog control attributes
  private subscriptions: Subscription[] = [];
  private chatListEffectRef: EffectRef | null = null;
  private capabilitiesEffectRef: EffectRef | null = null;

  public stepKeys: ShareStepKey[] = [];
  public steps: string[] = [];
  public stepIcons: string[] = [];
  public activeStep: number = 0;
  public get activeStepKey(): ShareStepKey | null {
    return this.stepKeys[this.activeStep] ?? null;
  }
  public get activeHelpStepIndex(): number {
    const activeKey = this.activeStepKey;
    return activeKey ? DialogShareComponent.HELP_STEP_INDEX_BY_KEY[activeKey] : 0;
  }
  //#endregion dialog control attributes

  //#region form definitions
  public shareNewsForm: FormGroup = this.formBuilder.group({
    recipients: ['all', [Validators.required]],
    is_recipients_all_push: [false],
    title: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
    message: ['', [Validators.required, Validators.minLength(1), this.richTextPlainTextMaxLengthValidator(1000)]],
    files: [[]],
    startDate: [null],
    endDate: [null, [this.endDateInFutureControlValidator()]],
    allowComments: [true],
    allowSharing: [false],
    is_important: [false],
    groups: [[]],
    users: [[]],
    participantsUserIDs: [[]],
    participantsGroupIDs: [[]],
    pushUserIDs: [[]],
    pushGroupIDs: [[]],
  }, {validators: [this.endDateAfterStartDateValidator()]});

  public shareChatForm: FormGroup = this.formBuilder.group({
    roomSearch: [''],
    selectedRooms: new FormArray([], [this.atLeastOneSelected()]),
  });

  public shareMailForm: FormGroup = this.formBuilder.group({
    recipientEmail: ['', [Validators.required, Validators.email]],
    subject: ['', [Validators.required, Validators.minLength(1)]],
    message: ['', [Validators.required, Validators.minLength(1)]],
    files: [[]],
  });

  public shareTvForm: FormGroup = this.formBuilder.group({
    title: ['', [Validators.required, Validators.minLength(1)]],
    internalNote: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(30)]],
    message: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1000)]],
    files: [[], [Validators.maxLength(1)]],
  });
  //#endregion form definitions

  //#region chat form attributes
  private previousChatRoomString: WritableSignal<string | null> = signal(null);
  public selectableChatRooms: WritableSignal<ChatListResource[]> = signal<ChatListResource[]>([]);
  //#endregion chat form attributes

  //#region news form attributes
  public selectableNewsFiles: WritableSignal<FileStorageResource[]> = signal<FileStorageResource[]>([]);
  //#endregion news form attributes

  //#region mail form attributes
  public selectableMailFiles: WritableSignal<FileStorageResource[]> = signal<FileStorageResource[]>([]);
  //#endregion mail form attributes

  //#region tv form attributes
  public selectableTvFiles: WritableSignal<FileStorageResource[]> = signal<FileStorageResource[]>([]);
  //#endregion tv form attributes

  //#region recipients
  public participantsUserIDs: number[] = [];
  public participantsGroupIDs: number[] = [];
  public pushUserIDs: number[] = [];
  public pushGroupIDs: number[] = [];
  public isUserGroupSelectionError: boolean = false;
  public showSchedulingValidationErrors: boolean = false;
  //#endregion recipients
  public shareLink: string = '';

  public constructor() {
    this.chatListEffectRef = effect(() => {
      this.syncSelectableChatRooms(this.chatStore.chatList());
    });

    this.capabilitiesEffectRef = effect(() => {
      this.capabilitiesService.capabilities();
      this.initializeSteps();
    });
  }

  //#region dialog functions
  public ngOnInit(): void {
    this.ensureRecipientsSelectionMatchesCapabilities();
    this.fillNewsFormValues();
    this.fillChatFormValues();
    this.fillMailFormValues();
    this.fillTvFormValues();
    this.shareLink = this.buildShareLink();

    this.subscriptions.push(
      this.layoutService.scrolledToEnd.subscribe({
        next: (scrolledToEnd: boolean | string) => {
          const chatStepIndex = this.stepKeys.indexOf('chat');
          const chatStepScrollTarget = chatStepIndex >= 0 ? `share-page-${chatStepIndex}` : null;
          if (
            this.canUseChatModuleInShareDialog() &&
            this.activeStepKey === 'chat' &&
            typeof scrolledToEnd === 'string' &&
            chatStepScrollTarget !== null &&
            scrolledToEnd === chatStepScrollTarget
          ) {
            untracked(() => {
              this.chatStore.loadChatList();
            });
          }
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.chatListEffectRef?.destroy();
    this.capabilitiesEffectRef?.destroy();
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public setStep(index: number): void {
    this.activeStep = index;
  }
  //#endregion dialog functions

  //#region submit functions
  public createNews(): void {
    if (!this.canCreateNewsInShareDialog()) {
      return;
    }

    this.isUserGroupSelectionError = false;
    const recipientsAll: boolean = this.shareNewsForm.controls['recipients'].value === 'all';
    if (recipientsAll && !this.canCreateForAllRecipients()) {
      this.isUserGroupSelectionError = true;
      return;
    }

    const allowedParticipantsUserIDs = this.canCreateForUserRecipients() ? this.participantsUserIDs : [];
    const allowedParticipantsGroupIDs = this.canCreateForGroupRecipients() ? this.participantsGroupIDs : [];
    const allowedPushUserIDs = this.canCreateForUserRecipients() ? this.pushUserIDs : [];
    const allowedPushGroupIDs = this.canCreateForGroupRecipients() ? this.pushGroupIDs : [];

    if (!recipientsAll && allowedParticipantsUserIDs.length === 0 && allowedParticipantsGroupIDs.length === 0) {
      this.isUserGroupSelectionError = true;
      return;
    }

    if (this.shareNewsForm.invalid) {
      this.showSchedulingValidationErrors = true;
      this.shareNewsForm.markAllAsTouched();
      this.shareNewsForm.controls['startDate'].markAsDirty();
      this.shareNewsForm.controls['endDate'].markAsDirty();
      return;
    }

    const selectedFiles: FileStorageResource[] = this.shareNewsForm.controls['files'].value ?? [];
    const fileStorageIDs: number[] = Array.from(
      new Set(
        selectedFiles
          .map((file) => file.id)
          .filter((id) => id > 0)
      )
    );

    const groupIDs = recipientsAll ? [] : allowedParticipantsGroupIDs;
    const userIDs = recipientsAll ? [] : this.getParticipantsUserIDsIncludingCurrentUser(allowedParticipantsUserIDs);
    const notifyGroupIDs = recipientsAll && this.shareNewsForm.controls['is_recipients_all_push'].value ? [0] : allowedPushGroupIDs;
    const notifyUserIDs = recipientsAll ? [] : allowedPushUserIDs;

    const payload: NewsCreateRequest = {
      title: this.shareNewsForm.controls['title'].value ?? '',
      message: this.shareNewsForm.controls['message'].value ?? '',
      visible_from: this.shareNewsForm.controls['startDate'].value ? new Date(this.shareNewsForm.controls['startDate'].value).toISOString() : null,
      visible_to: this.shareNewsForm.controls['endDate'].value ? new Date(this.shareNewsForm.controls['endDate'].value).toISOString() : null,
      commentable: this.shareNewsForm.controls['allowComments'].value ?? true,
      shareable: this.shareNewsForm.controls['allowSharing'].value ?? false,
      notify_users: notifyUserIDs.length > 0 || notifyGroupIDs.length > 0,
      notify_important: !!this.shareNewsForm.controls['is_important'].value,
      group_ids: groupIDs,
      notify_group_ids: notifyGroupIDs,
      user_ids: userIDs,
      notify_user_ids: notifyUserIDs,
      file_storage_ids: fileStorageIDs,
    };

    this.newsStore.createPost(payload, () => {
      this.toasterService.success(
        this.languageService.getTranslationByKey('SUCCESS'),
        this.languageService.getTranslationByKey('SHARESUCCEEDED')
      );
      this.close();
    });
  }

  public shareByMail(): void {
    const selectedFiles: FileStorageResource[] = this.shareMailForm.controls['files'].value ?? [];
    const fileStorageIDs: number[] = Array.from(
      new Set(
        selectedFiles
          .map((file) => file.id)
          .filter((id) => id > 0)
      )
    );

    this.apiService.shareByMail({
      recipient_email: this.shareMailForm.controls['recipientEmail'].value ?? '',
      subject: this.shareMailForm.controls['subject'].value ?? '',
      body: this.shareMailForm.controls['message'].value ?? '',
      file_storage_ids: fileStorageIDs,
    })
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.close();
      }, error: (error) => {
        this.errorService.setError(this.languageService.getTranslationByKey('MAILSHAREERROR'), error, true, false);
      }
    });
  }

  public shareToChat(share: boolean): void {
    const source = this.getNormalizedSource();
    const message = [source.title, source.messageHtml].filter((value) => value.trim() !== '').join('\n');

    const selectedRooms: ChatListResource[] = [];
    ((this.shareChatForm.controls['selectedRooms'] as FormArray).controls ?? [])
    .forEach((control, index) => {
      if(control.value === true) {
        selectedRooms.push(this.selectableChatRooms()[index]);
      }
    });

    if (selectedRooms.length === 0) {
      return;
    }

    const operations = selectedRooms.flatMap((room) => ([
      ...source.fileStorageIds.map((fileStorageId) => ({
        chatId: room.id,
        payload: {
          message: null,
          file_storage_ids: [fileStorageId],
          forwarded: false,
          reply_to: null
        }
      })),
      {
        chatId: room.id,
        payload: {
          message: message !== '' ? message : null,
          file_storage_ids: [],
          forwarded: false,
          reply_to: null
        }
      }
    ]));

    let failureCount = 0;
    this.subscriptions.push(
      from(operations)
        .pipe(
          concatMap((operation) =>
            this.apiService.addChatMessage(operation.chatId, operation.payload).pipe(
              catchError(() => {
                failureCount += 1;
                return of([]);
              })
            )
          ),
          toArray(),
          map(() => operations.length)
        )
        .subscribe({
          next: (operationCount) => {
            if (failureCount > 0) {
              this.errorService.setError(
                this.languageService.getTranslationByKey('ERROR'),
                `Failed to send ${failureCount} of ${operationCount} chat share operations.`,
                true,
                false
              );
              return;
            }

            if(share) {
              this.toasterService.success(
                this.languageService.getTranslationByKey('SUCCESS'),
        this.languageService.getTranslationByKey('SHARESUCCEEDED')
              );
              this.router.navigate([`/chat/detail/${selectedRooms[0].id}/0`]);
            } else {
              this.toasterService.success(
                this.languageService.getTranslationByKey('SUCCESS'),
        this.languageService.getTranslationByKey('SHARESUCCEEDED')
              );
              this.close();
            }
          }
        })
    );
  }

  public shareTvPreview(): void {
    const data = {
      title: this.shareTvForm.controls['title'].value,
      description: this.shareTvForm.controls['message'].value,
      files: this.shareTvForm.controls['files'].value
    };
    localStorage.setItem("contentPreviewData", JSON.stringify(data));
    window.alert('TODO: implement TV preview link opening');
  }

  public shareTv(): void {
    window.alert('TODO: implement TV share');
  }

  public copyShareLink(): void {
    const link = this.shareLink.trim();
    if (!link) {
      return;
    }

    const onSuccess = () => {
      this.toasterService.success(
        this.languageService.getTranslationByKey('EXTERNALLINK'),
        this.languageService.getTranslationByKey('COPIED')
      );
    };

    const onError = () => {
      this.toasterService.error(
        this.languageService.getTranslationByKey('EXTERNALLINK'),
        this.languageService.getTranslationByKey('COPYFAILED')
      );
    };

    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(onSuccess).catch(onError);
      return;
    }

    try {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (copied) {
        onSuccess();
      } else {
        onError();
      }
    } catch {
      onError();
    }
  }
  //#endregion submit functions

  //#region fill form values
  private fillNewsFormValues(): void {
    const source = this.getNormalizedSource();
    this.shareNewsForm.controls['title'].setValue(source.title);
    this.shareNewsForm.controls['message'].setValue(source.messageHtml);
    this.selectableNewsFiles.set(source.selectableDocuments);
    this.shareNewsForm.controls['files'].setValue(source.selectableDocuments);
    this.participantsUserIDs = [];
    this.participantsGroupIDs = [];
    this.pushUserIDs = [];
    this.pushGroupIDs = [];
    this.shareNewsForm.controls['participantsUserIDs'].setValue(this.participantsUserIDs);
    this.shareNewsForm.controls['participantsGroupIDs'].setValue(this.participantsGroupIDs);
    this.shareNewsForm.controls['pushUserIDs'].setValue(this.pushUserIDs);
    this.shareNewsForm.controls['pushGroupIDs'].setValue(this.pushGroupIDs);
    this.shareNewsForm.controls['title'].markAsDirty();
    this.shareNewsForm.controls['message'].markAsDirty();
    this.additionalFormChangesHandler();
  }

  private fillChatFormValues(): void {
    const roomSearch: string = (this.shareChatForm.controls['roomSearch'].value ?? '').trim();
    this.previousChatRoomString.set(roomSearch);
  }

  private fillMailFormValues(): void {
    const source = this.getNormalizedSource();
    this.shareMailForm.controls['subject'].setValue(source.title);
    this.shareMailForm.controls['message'].setValue(source.messageHtml);
    this.selectableMailFiles.set(source.selectableDocuments);
    this.shareMailForm.controls['files'].setValue(source.selectableDocuments);
  }

  private fillTvFormValues(): void {
    const source = this.getNormalizedSource();
    this.shareTvForm.controls['title'].setValue(source.title);
    this.shareTvForm.controls['message'].setValue(source.messageHtml);
    this.selectableTvFiles.set(source.selectableDocuments);
    this.shareTvForm.controls['files'].setValue(source.selectableDocuments);
  }
  //#endregion fill form values

  //#region formHelpers
  //#region chat form helpers
  public filterResults(): void {
    const roomSearch: string = (this.shareChatForm.controls['roomSearch'].value ?? '').trim();
    if (roomSearch !== this.previousChatRoomString()) {
      this.previousChatRoomString.set(roomSearch);
      this.chatStore.setChatQuery(roomSearch);
      return;
    }

    if (this.chatStore.chatList().length === 0) {
      this.chatStore.loadChatList();
    }
  }

  public getFormArray(): FormGroup {
    return (this.shareChatForm.controls['selectedRooms'] as FormGroup);
  }

  public selectAllRooms(): void {
    (this.shareChatForm.controls['selectedRooms'] as FormArray).controls
    .forEach((control) => {
      control.setValue(true);
    });
  }

  public removeAllSelectedRooms(): void {
    (this.shareChatForm.controls['selectedRooms'] as FormArray).controls
    .forEach((control) => {
      control.setValue(false);
    });
  }

  private atLeastOneSelected(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      const selected = formArray.controls.map(ctrl => ctrl.value).find(value => value === true);
      return selected ? null : {'atLeastOne': false};
    };
  }

  private parseDateValue(raw: unknown): Date | null {
    if (!raw) {
      return null;
    }
    const date = raw instanceof Date ? raw : new Date(raw as string);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getPlainTextLength(value: unknown): number {
    if (!value || typeof value !== 'string') {
      return 0;
    }

    const withoutTags = value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ');

    return withoutTags.trim().length;
  }

  private richTextPlainTextMaxLengthValidator(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const actualLength = this.getPlainTextLength(control.value);
      return actualLength > max
        ? {maxPlainTextLength: {requiredLength: max, actualLength}}
        : null;
    };
  }

  private endDateAfterStartDateValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startDate = this.parseDateValue(group.get('startDate')?.value);
      const endDate = this.parseDateValue(group.get('endDate')?.value);
      if (!startDate || !endDate) {
        return null;
      }
      return endDate > startDate ? null : {endDateBeforeOrEqualStartDate: true};
    };
  }

  private endDateInFutureControlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const endDate = this.parseDateValue(control.value);
      if (!endDate) {
        return null;
      }
      return endDate > new Date() ? null : {endDateNotInFuture: true};
    };
  }

  public isEndDateFutureInvalid(): boolean {
    const endDateControl = this.shareNewsForm.controls['endDate'];
    const hasInteraction = endDateControl.dirty || endDateControl.touched || this.showSchedulingValidationErrors;
    return !!endDateControl?.errors?.['endDateNotInFuture'] && hasInteraction;
  }

  public isEndDateOrderInvalid(): boolean {
    const startDateControl = this.shareNewsForm.controls['startDate'];
    const endDateControl = this.shareNewsForm.controls['endDate'];
    const hasInteraction = startDateControl.dirty || startDateControl.touched || endDateControl.dirty || endDateControl.touched || this.showSchedulingValidationErrors;
    return !!this.shareNewsForm.errors?.['endDateBeforeOrEqualStartDate'] && hasInteraction;
  }

  public hasSchedulingError(): boolean {
    return this.isEndDateFutureInvalid() || this.isEndDateOrderInvalid();
  }

  public getSchedulingErrorMessage(): string {
    if (this.isEndDateFutureInvalid()) {
      return this.languageService.getTranslationByKey('ENDDATEMUSTBEINTHEFUTURE');
    }
    if (this.isEndDateOrderInvalid()) {
      return this.languageService.getTranslationByKey('ENDDATEMUSTBEAFTERSTARTDATE');
    }
    return '';
  }

  public canShareNews(): boolean {
    if (!this.canCreateNewsInShareDialog()) {
      return false;
    }
    if (this.shareNewsForm.invalid) {
      return false;
    }
    if (this.shareNewsForm.controls['recipients'].value === 'all') {
      return this.canCreateForAllRecipients();
    }
    const canUseSelection = this.canCreateForUserRecipients() || this.canCreateForGroupRecipients();
    if (!canUseSelection) {
      return false;
    }

    const allowedUserIDs = this.canCreateForUserRecipients() ? this.participantsUserIDs : [];
    const allowedGroupIDs = this.canCreateForGroupRecipients() ? this.participantsGroupIDs : [];
    return allowedUserIDs.length > 0 || allowedGroupIDs.length > 0;
  }

  public getNewsMessageCharCount(): number {
    return this.getPlainTextLength(this.shareNewsForm.controls['message'].value);
  }

  private additionalFormChangesHandler(): void {
    this.subscriptions.push(
      this.shareNewsForm.valueChanges.subscribe(() => {
        if (this.shareNewsForm.controls['recipients'].value === 'all') {
          this.participantsUserIDs = [];
          this.participantsGroupIDs = [];
          this.pushUserIDs = [];
          this.pushGroupIDs = [];
          this.shareNewsForm.controls['participantsUserIDs'].setValue([], {emitEvent: false});
          this.shareNewsForm.controls['participantsGroupIDs'].setValue([], {emitEvent: false});
          this.shareNewsForm.controls['pushUserIDs'].setValue([], {emitEvent: false});
          this.shareNewsForm.controls['pushGroupIDs'].setValue([], {emitEvent: false});
          this.isUserGroupSelectionError = false;
        } else if (this.shareNewsForm.controls['recipients'].value === 'selection') {
          if (!this.canCreateForSelectionRecipients()) {
            this.shareNewsForm.controls['recipients'].setValue(this.getDefaultRecipientsMode(), {emitEvent: false});
            return;
          }
          this.shareNewsForm.controls['is_recipients_all_push'].setValue(false, {emitEvent: false});
        }
      })
    );
  }

  public canCreateNewsInShareDialog(): boolean {
    return this.canCreateForAllRecipients() || this.canCreateForSelectionRecipients();
  }

  public canCreateForAllRecipients(): boolean {
    return !!this.capabilitiesService.capabilities().news?.create_for_all;
  }

  public canCreateForUserRecipients(): boolean {
    return !!this.capabilitiesService.capabilities().news?.create_for_user;
  }

  public canCreateForGroupRecipients(): boolean {
    return !!this.capabilitiesService.capabilities().news?.create_for_group;
  }

  public canCreateForSelectionRecipients(): boolean {
    return this.canCreateForUserRecipients() || this.canCreateForGroupRecipients();
  }

  public canUseChatModuleInShareDialog(): boolean {
    return this.capabilitiesService.capabilities().chat !== null;
  }

  private initializeSteps(): void {
    const previousActiveStepKey = this.activeStepKey;
    const stepDefinitions: {key: ShareStepKey; label: string; icon: string}[] = [
      {
        key: 'news',
        label: this.languageService.getTranslationByKey('SHAREUSERSGROUPSMENUTEXT'),
        icon: 'group'
      },
      {
        key: 'chat',
        label: this.languageService.getTranslationByKey('SHARECHATMENUTEXT'),
        icon: 'chat'
      },
      {
        key: 'mail',
        label: this.languageService.getTranslationByKey('SHAREMAILMENUTEXT'),
        icon: 'mail'
      },
      {
        key: 'link',
        label: this.languageService.getTranslationByKey('SHARELINKMENUTEXT'),
        icon: 'link'
      },
      // TODO: leave the share to tv option out for now
      // {
      //   key: 'tv',
      //   label: this.languageService.getTranslationByKey('SHARETVMENUTEXT'),
      //   icon: 'tv'
      // }
    ];

    const visibleSteps = stepDefinitions.filter((step) => {
      if (step.key === 'news') {
        return this.canCreateNewsInShareDialog();
      }
      if (step.key === 'chat') {
        return this.canUseChatModuleInShareDialog();
      }
      return true;
    });
    this.stepKeys = visibleSteps.map((step) => step.key);
    this.steps = visibleSteps.map((step) => step.label);
    this.stepIcons = visibleSteps.map((step) => step.icon);

    if (previousActiveStepKey) {
      const nextIndex = this.stepKeys.findIndex((key) => key === previousActiveStepKey);
      if (nextIndex >= 0) {
        this.activeStep = nextIndex;
        return;
      }
    }

    if (this.activeStep >= this.steps.length) {
      this.activeStep = 0;
    }
  }

  private ensureRecipientsSelectionMatchesCapabilities(): void {
    const mode = this.getDefaultRecipientsMode();
    this.shareNewsForm.controls['recipients'].setValue(mode, {emitEvent: false});
  }

  private getDefaultRecipientsMode(): 'all' | 'selection' {
    if (this.canCreateForAllRecipients()) {
      return 'all';
    }
    return 'selection';
  }

  private getParticipantsUserIDsIncludingCurrentUser(userIDs: number[]): number[] {
    const currentUserId = this.profileService.profile()?.user?.id;
    if (!currentUserId) {
      return userIDs;
    }
    return Array.from(new Set([...userIDs, currentUserId]));
  }

  private syncSelectableChatRooms(chats: ChatListResource[]): void {
    const selectedRoomIDs = new Set<number>();
    ((this.shareChatForm.controls['selectedRooms'] as FormArray).controls ?? [])
      .forEach((control, index) => {
        if (control.value === true && this.selectableChatRooms()[index]) {
          selectedRoomIDs.add(this.selectableChatRooms()[index].id);
        }
      });

    this.selectableChatRooms.set(chats);

    const controls = chats.map((room) => new FormControl(selectedRoomIDs.has(room.id)));
    this.shareChatForm.setControl('selectedRooms', new FormArray(controls, [this.atLeastOneSelected()]));
  }

  //#endregion chat form helpers

  //#region source normalization
  private getInputSource(): NewsResource | KnowledgebaseContentResource | TYPE.CommunityDetailEntry | null {
    return this.data ?? this.communityDetailEntry;
  }

  private isNewsResource(source: unknown): source is NewsResource {
    return !!source && Object.prototype.hasOwnProperty.call(source, 'creator') && Object.prototype.hasOwnProperty.call(source, 'message');
  }

  private isKnowledgebaseContentResource(source: unknown): source is KnowledgebaseContentResource {
    return !!source && Object.prototype.hasOwnProperty.call(source, 'content') && Object.prototype.hasOwnProperty.call(source, 'editor');
  }

  private isCommunityDetailEntry(source: unknown): source is TYPE.CommunityDetailEntry {
    return !!source && Object.prototype.hasOwnProperty.call(source, 'message_web') && Object.prototype.hasOwnProperty.call(source, 'channel_id');
  }

  private getNormalizedSource(): {
    title: string;
    messageHtml: string;
    fileStorageIds: number[];
    selectableDocuments: FileStorageResource[];
    sourceKind: 'news' | 'knowledgebase' | 'community' | 'none';
  } {
    const source = this.getInputSource();
    if (!source) {
      return {
        title: '',
        messageHtml: '',
        fileStorageIds: [],
        selectableDocuments: [],
        sourceKind: 'none'
      };
    }

    if (this.isNewsResource(source)) {
      const selectableDocuments = source.files ?? [];
      return {
        title: source.title ?? '',
        messageHtml: source.message ?? '',
        fileStorageIds: (source.files ?? []).map((file) => file.id).filter((id) => id > 0),
        selectableDocuments,
        sourceKind: 'news'
      };
    }

    if (this.isKnowledgebaseContentResource(source)) {
      const selectableDocuments = source.files ?? [];
      return {
        title: source.title ?? '',
        messageHtml: source.content ?? '',
        fileStorageIds: (source.files ?? []).map((file) => file.id).filter((id) => id > 0),
        selectableDocuments,
        sourceKind: 'knowledgebase'
      };
    }

    if (this.isCommunityDetailEntry(source)) {
      const selectableDocuments = source.files ?? [];
      return {
        title: source.title ?? '',
        messageHtml: source.message_web ?? '',
        fileStorageIds: selectableDocuments
          .map((file) => file.id)
          .filter((id) => id > 0),
        selectableDocuments,
        sourceKind: 'community'
      };
    }

    return {
      title: '',
      messageHtml: '',
      fileStorageIds: [],
      selectableDocuments: [],
      sourceKind: 'none'
    };
  }

  private buildShareLink(): string {
    const source = this.getInputSource();
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const currentPath = this.router.url || '/';

    if (!source) {
      return `${origin}${currentPath}`;
    }

    if (this.isNewsResource(source)) {
      return `${origin}/news/list/${source.id}`;
    }

    if (this.isKnowledgebaseContentResource(source)) {
      return `${origin}${currentPath}`;
    }

    if (this.isCommunityDetailEntry(source)) {
      return `${origin}/community/list/topic/${source.channel_id}`;
    }

    return `${origin}${currentPath}`;
  }
  //#endregion source normalization
  //#endregion formHelpers
}
